import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiRequest } from '../utils/api';

interface ProductDetailStatus {
  id: number;
  statusName: string;
  description: string | null;
  color: string | null;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export default function ProductDetailStatusPage() {
  const [items, setItems] = useState<ProductDetailStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState({
    statusName: '',
    description: '',
    color: '',
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProductDetailStatus | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const showError = (message: string) =>
    setSnackbar({ open: true, message, severity: 'error' });
  const showSuccess = (message: string) =>
    setSnackbar({ open: true, message, severity: 'success' });

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await apiRequest('/product-detail-status', { method: 'GET' });
      const data = (response.data || []) as ProductDetailStatus[];
      setItems(data);
    } catch (error) {
      console.error('Error loading product detail statuses:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to load product detail statuses'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleOpenCreate = () => {
    setIsEdit(false);
    setSelectedId(null);
    setForm({ statusName: '', description: '', color: '' });
    setOpenDialog(true);
  };

  const handleOpenEdit = (item: ProductDetailStatus) => {
    setIsEdit(true);
    setSelectedId(item.id);
    setForm({
      statusName: item.statusName,
      description: item.description || '',
      color: item.color || '',
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.statusName) {
      showError('Status Name is required');
      return;
    }

    setSaving(true);
    try {
      if (!isEdit) {
        const response = await apiRequest('/product-detail-status', {
          method: 'PUT',
          body: JSON.stringify({
            statusName: form.statusName,
            description: form.description || null,
            color: form.color || null,
            isActive: true,
          }),
        });
        const data = response.data as { productDetailStatus: ProductDetailStatus };
        setItems((prev) => [data.productDetailStatus, ...prev]);
        showSuccess('Product detail status created');
      } else if (selectedId != null) {
        const response = await apiRequest(`/product-detail-status/${selectedId}`, {
          method: 'POST',
          body: JSON.stringify({
            statusName: form.statusName,
            description: form.description || null,
            color: form.color || null,
          }),
        });
        const data = response.data as { productDetailStatus: ProductDetailStatus };
        setItems((prev) =>
          prev.map((it) => (it.id === data.productDetailStatus.id ? data.productDetailStatus : it))
        );
        showSuccess('Product detail status updated');
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving product detail status:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to save product detail status'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (item: ProductDetailStatus) => {
    setDeleteTarget(item);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiRequest(`/product-detail-status/${deleteTarget.id}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
      showSuccess('Product detail status deleted');
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting product detail status:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to delete product detail status'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const displayedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ px: { xs: 1, sm: 0 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            Settings
          </Typography>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Product Detail Status
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={loadItems} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' }, whiteSpace: 'nowrap' }}
          >
            New Status
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        /* ── Mobile card list ── */
        <Box>
          {displayedItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No product detail statuses found
            </Typography>
          ) : (
            displayedItems.map((item, index) => (
              <Card key={item.id} variant="outlined" sx={{ mb: 1.5 }}>
                <CardContent sx={{ pb: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      #{index + 1 + page * rowsPerPage}
                    </Typography>
                    {item.color && (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                          border: '1px solid #ccc',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {item.statusName}
                    </Typography>
                  </Box>
                  {item.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {item.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}
                  </Typography>
                </CardContent>
                <Divider sx={{ mt: 1 }} />
                <CardActions sx={{ justifyContent: 'flex-end', py: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenEdit(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleOpenDelete(item)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            ))
          )}
          <TablePagination
            component="div"
            count={items.length}
            rowsPerPageOptions={[5, 10, 25]}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 1 }}
          />
        </Box>
      ) : (
        /* ── Desktop table ── */
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Status Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No product detail statuses found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedItems.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                      <TableCell>{item.statusName}</TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>
                        {item.color ? (
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              backgroundColor: item.color,
                              borderRadius: 1,
                              border: '1px solid #ccc',
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell>
                        {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleOpenEdit(item)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => handleOpenDelete(item)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={items.length}
            rowsPerPageOptions={[5, 10, 25, 50]}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{isEdit ? 'Edit Product Detail Status' : 'New Product Detail Status'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Status Name"
              value={form.statusName}
              onChange={(e) => setForm({ ...form, statusName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Color
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box
                  component="input"
                  type="color"
                  value={form.color || '#FF5733'}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  sx={{
                    width: 60,
                    height: 40,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    cursor: 'pointer',
                    padding: 0,
                    '&::-webkit-color-swatch-wrapper': { padding: 0 },
                    '&::-webkit-color-swatch': { border: 'none', borderRadius: 1 },
                  }}
                />
                <TextField
                  label="Hex code"
                  value={form.color}
                  onChange={(e) => {
                    let colorValue = e.target.value;
                    if (colorValue && !colorValue.startsWith('#')) colorValue = '#' + colorValue;
                    setForm({ ...form, color: colorValue });
                  }}
                  placeholder="#FF5733"
                  sx={{ flex: 1 }}
                  slotProps={{ htmlInput: { pattern: '^#[0-9A-Fa-f]{6}$', maxLength: 7 } }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !form.statusName}>
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Product Detail Status</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.statusName}</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
